import {
  Badge,
  Box,
  NumberInput,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconPackageOff } from "@tabler/icons-react";
import cx from "clsx";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import classes from "./ReceiveTicketUI.module.css";

// Chiều cao tối thiểu của bảng, thấp hơn mức này thì bảng bị bí quá
const TABLE_MIN_HEIGHT = 315;
// Chừa mép dưới màn hình, đã tính cả padding + viền dưới của khối bao ngoài
const TABLE_BOTTOM_GAP = 28;

const SERVICE_TYPE_COLORS: Record<string, string> = {
  "Bảo hành": "blue",
  "Hỗ trợ": "cyan",
  "Dịch vụ": "teal",
  "Bảo trì": "grape",
  "Nâng cấp": "indigo",
  "Vi phạm BH": "red",
};

const SERVICE_TYPE_OPTIONS = Object.keys(SERVICE_TYPE_COLORS).map((item) => ({
  label: item,
  value: item,
}));

// Ô trống hiển thị gạch ngang mờ thay vì để trắng, bảng đỡ bị "thủng"
const EmptyCell = () => <span className={classes.cellEmpty}>—</span>;

const isEmptyValue = (value: any) =>
  value === null || value === undefined || value === "";

const renderValue = (value: any) =>
  isEmptyValue(value) ? <EmptyCell /> : value;

const renderMoney = (value: any) =>
  isEmptyValue(value) ? <EmptyCell /> : `${Number(value).toLocaleString()} đ`;

const renderDate = (value: any) =>
  value && dayjs(value).isValid() ? (
    dayjs(value).format("DD-MM-YYYY")
  ) : (
    <EmptyCell />
  );

const ServiceTypeBadge = ({ value }: { value: any }) =>
  isEmptyValue(value) ? (
    <EmptyCell />
  ) : (
    <Badge
      color={SERVICE_TYPE_COLORS[value] ?? "gray"}
      variant="light"
      radius="sm"
      tt="none"
      fw={600}
    >
      {value}
    </Badge>
  );

const ProductWarrantyList = ({
  dataRequestFormSelectedDetail,
  warrantyReceiveTicketDetail,
  editAndSave,
  setEditAndSave,
  isEdit,
  setIsEdit,
  handleChangeValue,
  actionView,
  typeProduct,
  statusForm,
}: {
  dataRequestFormSelectedDetail: any;
  warrantyReceiveTicketDetail: any;
  editAndSave: any;
  setEditAndSave: any;
  isEdit: any;
  setIsEdit: any;
  handleChangeValue: any;
  actionView?: any;
  typeProduct: any;
  statusForm?: any;
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(TABLE_MIN_HEIGHT);

  const hasData =
    Array.isArray(dataRequestFormSelectedDetail) &&
    dataRequestFormSelectedDetail.length > 0;

  // Bảng là khối cuối trang nên kéo dài xuống hết phần màn hình còn trống,
  // tránh để thừa một mảng trắng lớn phía dưới.
  useEffect(() => {
    const updateHeight = () => {
      const wrap = wrapRef.current;

      if (!wrap) return;

      const { top } = wrap.getBoundingClientRect();
      setTableHeight(
        Math.max(window.innerHeight - top - TABLE_BOTTOM_GAP, TABLE_MIN_HEIGHT),
      );
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, [hasData]);

  // Ngày hẹn trả / số ngày hẹn trả chỉ sửa được khi phiếu đã nhập kho (gia hạn)
  const isReadOnlySchedule =
    actionView === "DETAIL" ||
    (actionView === "EDIT" && statusForm !== "DA_NHAP");
  // Các thông tin còn lại khoá lại khi xem chi tiết hoặc phiếu đã nhập kho
  const isReadOnlyInfo = actionView === "DETAIL" || statusForm === "DA_NHAP";

  if (!hasData) {
    return (
      <div
        ref={wrapRef}
        className={classes.tableWrap}
        style={{ height: tableHeight }}
      >
        <div className={classes.emptyState}>
          <IconPackageOff size={28} stroke={1.4} />
          <span className={classes.emptyStateTitle}>Chưa có sản phẩm nào</span>
          <Text size="11.5px">
            {actionView === "CREATE"
              ? "Chọn phiếu tiếp nhận yêu cầu để hiển thị danh sách sản phẩm cần tiếp nhận."
              : "Phiếu này chưa có sản phẩm nào được tiếp nhận."}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={classes.tableWrap}>
      <ScrollArea
        h={tableHeight}
        onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
      >
        <Table className={classes.dataTable} w={2900}>
          <Table.Thead
            className={cx(classes.stickyHead, {
              [classes.stickyHeadScrolled]: scrolled,
            })}
          >
            <Table.Tr>
              <Table.Th>Hẹn trả hàng</Table.Th>
              <Table.Th>Số ngày hẹn trả</Table.Th>
              <Table.Th>Phụ kiện kèm theo</Table.Th>
              <Table.Th>Loại dịch vụ</Table.Th>
              <Table.Th>Hóa đơn</Table.Th>
              <Table.Th>Serial</Table.Th>
              <Table.Th>Sản phẩm</Table.Th>
              <Table.Th>Mã sản phẩm</Table.Th>
              <Table.Th>Đơn vị tính</Table.Th>
              <Table.Th>Số lượng</Table.Th>
              <Table.Th>Thời hạn bảo hành</Table.Th>
              <Table.Th>Ghi nhận lỗi ban đầu</Table.Th>
              <Table.Th>Trạng thái serial</Table.Th>
              <Table.Th>Nhà cung cấp</Table.Th>
              <Table.Th>Người mua</Table.Th>
              <Table.Th className={classes.cellNumber}>Giá bán</Table.Th>
              <Table.Th className={classes.cellNumber}>Giá hiện tại</Table.Th>
              <Table.Th>Ghi chú</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {dataRequestFormSelectedDetail?.map((item: any, index: any) => (
              <Table.Tr key={index}>
                {isReadOnlySchedule ? (
                  <Table.Td w={150}>{renderDate(item.scheduleTime)}</Table.Td>
                ) : actionView === "CREATE" ? (
                  <Table.Td w={150}>{renderDate(item.entryDate)}</Table.Td>
                ) : (
                  <Table.Td w={150}>
                    <DatePickerInput
                      placeholder="Nhập ngày hẹn trả"
                      valueFormat="DD-MM-YYYY"
                      locale="vi"
                      size="xs"
                      radius="md"
                      value={
                        warrantyReceiveTicketDetail[index]?.scheduleTime
                          ? new Date(
                              warrantyReceiveTicketDetail[index]?.scheduleTime,
                            )
                          : null
                      }
                      minDate={new Date()}
                      onChange={(e: any) => {
                        const selectedDate = dayjs(e);
                        const today = dayjs();
                        const daysDifference = selectedDate.diff(today, "day");
                        handleChangeValue(
                          "scheduleTime",
                          e ? new Date(e ?? "").toDateString() : null,
                          Number(index),
                        );
                        handleChangeValue(
                          "daysReturn",
                          daysDifference,
                          Number(index),
                        );
                      }}
                    />
                  </Table.Td>
                )}
                {isReadOnlySchedule ? (
                  <Table.Td w={110}>{renderValue(item.daysReturn)}</Table.Td>
                ) : actionView === "CREATE" ? (
                  <Table.Td w={110}>{renderValue(item.attribute5)}</Table.Td>
                ) : (
                  <Table.Td w={110}>
                    <NumberInput
                      placeholder="Nhập số ngày hẹn trả hàng"
                      hideControls
                      size="xs"
                      radius="md"
                      value={
                        warrantyReceiveTicketDetail[index]?.daysReturn
                          ? warrantyReceiveTicketDetail[index]?.daysReturn
                          : null
                      }
                      onChange={(e) => {
                        handleChangeValue(
                          "daysReturn",
                          e ? Number(e) : null,
                          Number(index),
                        );
                        handleChangeValue(
                          "scheduleTime",
                          e
                            ? new Date(
                                dayjs(new Date())
                                  .add(Number(e), "day")
                                  .toDate(),
                              )
                            : null,
                          Number(index),
                        );
                      }}
                    />
                  </Table.Td>
                )}
                {isReadOnlyInfo ? (
                  <Table.Td w={150}>{renderValue(item.acessory)}</Table.Td>
                ) : (
                  <Table.Td
                    onMouseDown={(event: React.MouseEvent) =>
                      event.stopPropagation()
                    }
                    w={150}
                  >
                    <TextInput
                      placeholder="Nhập phụ kiện kèm theo"
                      size="xs"
                      radius="md"
                      value={warrantyReceiveTicketDetail[index]?.acessory}
                      onChange={(e: any) =>
                        handleChangeValue(
                          "acessory",
                          e ? e.currentTarget.value : null,
                          Number(index),
                        )
                      }
                    />
                  </Table.Td>
                )}
                {isReadOnlyInfo ? (
                  <Table.Td w={150}>
                    <ServiceTypeBadge
                      value={
                        warrantyReceiveTicketDetail[index]?.serviceType
                          ? warrantyReceiveTicketDetail[index]?.serviceType
                          : dataRequestFormSelectedDetail[
                              index
                            ]?.serviceType?.toString()
                      }
                    />
                  </Table.Td>
                ) : (
                  <Table.Td
                    onMouseDown={(event: React.MouseEvent) =>
                      event.stopPropagation()
                    }
                    w={150}
                  >
                    <Select
                      placeholder="Chọn loại dịch vụ"
                      size="xs"
                      radius="md"
                      data={SERVICE_TYPE_OPTIONS}
                      value={
                        warrantyReceiveTicketDetail[index]?.serviceType
                          ? warrantyReceiveTicketDetail[index]?.serviceType
                          : null
                      }
                      onChange={(e: any) =>
                        handleChangeValue(
                          "serviceType",
                          e ? e : "Bảo hành",
                          Number(index),
                        )
                      }
                    />
                  </Table.Td>
                )}
                <Table.Td w={125}>
                  {typeProduct === "HANG_HACOM" ? (
                    renderValue(item.orderCode)
                  ) : (
                    <Text size="12.5px" c="dimmed">
                      Không có hóa đơn
                    </Text>
                  )}
                </Table.Td>
                <Table.Td w={120}>{renderValue(item.serial)}</Table.Td>
                <Table.Td w={300}>
                  <Box w={275}>
                    <Tooltip label={item.itemName} multiline maw={420}>
                      <Text truncate="end" size="12.5px" fw={600}>
                        {item.itemName}
                      </Text>
                    </Tooltip>
                  </Box>
                </Table.Td>
                <Table.Td w={100}>{renderValue(item.itemCode)}</Table.Td>
                <Table.Td w={100}>{renderValue(item.uomName)}</Table.Td>
                <Table.Td w={70}>1</Table.Td>
                <Table.Td w={125}>
                  {isEmptyValue(item.warrantyTerm) ? (
                    <EmptyCell />
                  ) : (
                    `${item.warrantyTerm} tháng`
                  )}
                </Table.Td>
                <Table.Td w={200}>{renderValue(item.conclusionError)}</Table.Td>
                <Table.Td w={150}>
                  {isEmptyValue(item.serialStatus) ? (
                    <EmptyCell />
                  ) : (
                    <Badge color="gray" variant="light" radius="sm" tt="none">
                      {item.serialStatus}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td w={150}>{renderValue(item.supplierNum)}</Table.Td>
                <Table.Td w={150}>
                  {renderValue(item.customerOrderName)}
                </Table.Td>
                <Table.Td w={120} className={classes.cellNumber}>
                  {renderMoney(item.priceOnPriceList)}
                </Table.Td>
                <Table.Td w={120} className={classes.cellNumber}>
                  {renderMoney(item.latestPrice)}
                </Table.Td>
                {actionView === "DETAIL" ? (
                  <Table.Td w={200}>{renderValue(item.note)}</Table.Td>
                ) : (
                  <Table.Td
                    className={classes.cellEditable}
                    title="Nhấn đúp để nhập ghi chú"
                    onDoubleClick={() => {
                      setIsEdit((prev: any) => ({
                        ...prev,
                        note: true,
                      }));
                      setEditIndex(index);
                    }}
                    onMouseDown={(event: React.MouseEvent) =>
                      event.stopPropagation()
                    }
                    w={200}
                  >
                    {editIndex === Number(index) && isEdit.note ? (
                      <TextInput
                        placeholder="Nhập ghi chú"
                        size="xs"
                        radius="md"
                        autoFocus
                        value={
                          warrantyReceiveTicketDetail[index]?.note
                            ? warrantyReceiveTicketDetail[index]?.note
                            : null
                        }
                        onChange={(e) => {
                          handleChangeValue(
                            "note",
                            e ? e.currentTarget.value : null,
                            Number(index),
                          );
                        }}
                      />
                    ) : (
                      renderValue(warrantyReceiveTicketDetail[index]?.note)
                    )}
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </div>
  );
};

export default ProductWarrantyList;
